import React, { useEffect, useMemo, useState } from 'react';
import { X, RotateCcw } from 'lucide-react';
import { todayStr, isPast, fmtCivil, toCivil, startOfWeek } from '../dates';

// THE ROUNDS — the Daily Brief renovated (round-3 decision, docs/redesignContest.md).
// One Begin, zero decisions, a visible ending. Three frames:
//   1. The threshold — still and centered; the walk is chosen, not ambient.
//   2. The walk — one goal per step (click / Enter / j / k / arrows), each with
//      its plan line, a soft prose state, the single next action, and ONE quiet
//      fact line under Nancy's amended law: dates the world owns and absolute
//      completed counts ("4 this week") — never denominators, targets, or
//      streaks that break.
//   3. The ending — "That's the rounds." plus whatever dates are still asking
//      (overdue / slipped plans / deferred come due), then the door out.
// Vocabulary: plain labels everywhere a first-timer lands (plan, done, next);
// the house voice survives only in full sentences.
// Everything is derived from existing data. No schema, no server.

const DAY_MS = 24 * 60 * 60 * 1000;

// The plan line: the charter's first sentence, markdown stripped.
const planLineOf = (notes) => {
  if (!notes) return null;
  const line = notes
    .split('\n')
    .map((l) => l.replace(/^[>#\s*-]+/, '').replace(/\*\*?/g, '').trim())
    .find(Boolean);
  if (!line) return null;
  const m = line.match(/^.*?[.!?](?=["')\s]|$)/);
  return (m ? m[0] : line).trim();
};

// "— you, last Thursday": a human date for a finishing note's byline.
const relDay = (iso) => {
  if (!iso) return null;
  const then = new Date(iso);
  const now = new Date();
  const days = Math.floor(
    (new Date(now.getFullYear(), now.getMonth(), now.getDate()) -
      new Date(then.getFullYear(), then.getMonth(), then.getDate())) / DAY_MS
  );
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  const wd = then.toLocaleDateString(undefined, { weekday: 'long' });
  if (days < 7) return `last ${wd}`;
  if (days < 14) return `two ${wd}s ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export default function DailyBrief({ open, onClose, actions, completedActions, deferredActions, projects, goals, onOpenAction, onOpenDeferred, updateAction }) {
  // 0 = threshold · 1..N = one goal per step · N+1 = the ending
  const [step, setStep] = useState(0);

  const rounds = useMemo(() => {
    const now = Date.now();
    const today = todayStr();
    const weekStart = startOfWeek(today);
    const openActions = actions.filter(a => !a.completed);

    const scenes = goals
      .filter(g => g.active !== false)
      .map(goal => {
        const pids = projects.filter(p => p.goalId === goal.id && p.active !== false).map(p => p.id);
        const mine = openActions.filter(a => a.linkType === 'project' && pids.includes(a.linkId));
        if (!mine.length) return null; // only promises with open work make the walk

        // The single next action: starred, then planned-for-today, then in
        // motion, then your hand-arranged order.
        const rank = (a) => (a.pho ? 0 : a.doDate === today ? 1 : a.status === 'doing' ? 2 : 3);
        const next = [...mine].sort((a, b) => rank(a) - rank(b) || ((a.order ?? a.id) - (b.order ?? b.id)))[0];
        const nextProject = projects.find(p => p.id === next.linkId);

        const done = completedActions.filter(a => a.linkType === 'project' && pids.includes(a.linkId));
        const doneThisWeek = done.filter(a => a.completedDate && toCivil(new Date(a.completedDate)) >= weekStart).length;
        const dues = mine.map(a => a.dueDate).filter(Boolean).sort();
        const landing = done
          .filter(a => a.resolutionComment && a.completedDate)
          .sort((a, b) => new Date(b.completedDate) - new Date(a.completedDate))[0] || null;

        // Soft prose state — the house voice, full sentences only.
        const touched = Math.max(0,
          ...mine.map(a => new Date(a.lastTouchedAt || a.createdDate || 0).getTime()),
          ...done.map(a => new Date(a.completedDate || 0).getTime()));
        const daysQuiet = touched > 0 ? Math.floor((now - touched) / DAY_MS) : null;
        const doing = mine.filter(a => a.status === 'doing').length;
        let prose;
        if (doing > 0) prose = 'In motion — something here is being worked right now.';
        else if (daysQuiet !== null && daysQuiet >= 10) prose = `Quiet ${daysQuiet} days. Still yours?`;
        else if (daysQuiet !== null && daysQuiet <= 1) prose = daysQuiet === 0 ? 'Touched today — still warm.' : 'Touched yesterday — still warm.';
        else prose = 'Waiting quietly. Nothing here is lost.';

        // ONE quiet fact line — Nancy's amended law. Dates the world owns;
        // counts stated absolutely. A week with nothing done says nothing.
        const facts = [];
        if (dues[0]) facts.push(`Due ${fmtCivil(dues[0], { month: 'short', day: 'numeric' })}`);
        if (doneThisWeek > 0) facts.push(`${doneThisWeek} this week`);

        return {
          goal,
          plan: planLineOf(goal.notes),
          prose,
          next,
          crumb: nextProject ? nextProject.name : null,
          factLine: facts.length ? facts.join(' · ') : null,
          landing,
        };
      })
      .filter(Boolean);

    const overdue = openActions.filter(a => isPast(a.dueDate));
    const missedPlans = openActions.filter(a => isPast(a.doDate) && !isPast(a.dueDate) && !a.pho);
    const dueDeferred = (deferredActions || []).filter(a => a.deferToDate && new Date(a.deferToDate).getTime() <= now);
    return { scenes, overdue, missedPlans, dueDeferred };
  }, [actions, completedActions, deferredActions, projects, goals]);

  const last = rounds.scenes.length + 1; // index of the ending frame

  // Each morning starts at the threshold.
  useEffect(() => { if (open) setStep(0); }, [open]);

  // The walk: j/k, arrows, Enter. Captured so the app's global shortcuts
  // (k = board, etc.) stay quiet while the rounds are open; B and Esc pass
  // through untouched — DailyFocusTool still owns open/close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      const fwd = e.key === 'j' || e.key === 'J' || e.key === 'ArrowDown' || e.key === 'ArrowRight' || e.key === 'Enter';
      const back = e.key === 'k' || e.key === 'K' || e.key === 'ArrowUp' || e.key === 'ArrowLeft';
      if (fwd) { e.preventDefault(); e.stopPropagation(); setStep(s => Math.min(s + 1, last)); }
      else if (back) { e.preventDefault(); e.stopPropagation(); setStep(s => Math.max(s - 1, 0)); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, last]);

  if (!open) return null;

  const cur = Math.min(step, last);
  const advance = () => setStep(s => Math.min(s + 1, last));
  const scene = cur >= 1 && cur <= rounds.scenes.length ? rounds.scenes[cur - 1] : null;
  const n = rounds.scenes.length;
  const leftovers = rounds.overdue.length + rounds.missedPlans.length + rounds.dueDeferred.length > 0;

  const dateLine = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  // Scene atmosphere, mixed from the live theme so every theme tints its own rounds.
  const tint = scene
    ? (scene.prose.startsWith('Quiet') || scene.prose.startsWith('Waiting')
      ? 'linear-gradient(160deg, color-mix(in srgb, var(--color-textMuted) 6%, var(--color-background)) 0%, var(--color-background) 60%)'
      : 'linear-gradient(160deg, color-mix(in srgb, var(--color-primary) 5%, var(--color-background)) 0%, var(--color-background) 60%)')
    : 'var(--color-background)';

  const Label = ({ children }) => (
    <p className="text-[10px] uppercase tracking-[0.16em] themed-text-muted font-semibold mb-2">{children}</p>
  );

  return (
    <div className="fixed inset-0 z-[85] overflow-y-auto themed-background daily-brief-in" style={{ background: tint }}>
      <style>{`
        .rounds-step-in { animation: rounds-step-in 0.35s cubic-bezier(0.16, 1, 0.3, 1); }
        @keyframes rounds-step-in { from { opacity: 0; transform: translateY(12px); } }
      `}</style>

      <button
        onClick={onClose}
        className="fixed top-4 right-5 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium themed-text-muted hover:themed-text border themed-border themed-background shadow-sm"
        title="Close (Esc or B)"
      >
        <X size={11} /> skip for now
      </button>

      <div className="min-h-full flex items-center justify-center px-8 py-20">
        <div key={cur} className="rounds-step-in w-full max-w-[640px]">

          {/* ══ 1 · The threshold ══ */}
          {cur === 0 && (
            <div className="text-center max-w-[520px] mx-auto">
              <p className="text-[11px] uppercase tracking-[0.18em] themed-text-muted font-semibold mb-6">{dateLine}</p>
              <h1 className="text-5xl themed-text mb-5" style={{ lineHeight: 1.08 }}>The morning rounds</h1>
              <p className="text-[15px] themed-text-secondary mb-9" style={{ lineHeight: 'var(--lh-note, 1.75)' }}>
                {n === 0
                  ? 'No promises have open work this morning.'
                  : `${n} promise${n === 1 ? '' : 's'}, about two minutes, and it ends.`}
              </p>
              <button
                onClick={advance}
                className="themed-button-primary rounded-full px-8 py-2.5 text-sm font-medium"
              >
                Begin
              </button>
              <button onClick={onClose} className="block mx-auto mt-4 text-[12px] themed-text-muted hover:themed-text">
                skip to your goals →
              </button>
            </div>
          )}

          {/* ══ 2 · The walk — one goal per step ══ */}
          {scene && (
            <div onClick={advance} className="cursor-pointer select-text">
              <h1 className="text-4xl themed-text mb-4" style={{ lineHeight: 1.12 }}>{scene.goal.name}</h1>
              {scene.plan && (
                <p className="text-[17px] italic themed-text-secondary mb-6" style={{ lineHeight: 1.55, maxWidth: '56ch' }}>{scene.plan}</p>
              )}
              <p className="text-[14px] themed-text-secondary mb-7" style={{ lineHeight: 'var(--lh-note, 1.75)' }}>{scene.prose}</p>

              {scene.landing && (
                <div className="mb-7">
                  <Label>Done</Label>
                  <blockquote className="text-[15px] themed-text" style={{ lineHeight: 1.5 }}>
                    “{scene.landing.resolutionComment}”
                    <span className="block text-[12px] themed-text-muted mt-1 not-italic">— you, {relDay(scene.landing.completedDate)}</span>
                  </blockquote>
                </div>
              )}

              <div className="pt-4 border-t themed-border-light">
                <Label>Next</Label>
                <div className="flex items-center justify-between gap-5">
                  <span className="text-[15px] font-medium themed-text" style={{ lineHeight: 1.45 }}>
                    {scene.next.pho && <span className="mr-1" style={{ color: 'var(--color-pho)' }}>✦</span>}
                    {scene.next.text}
                    {scene.crumb && <span className="block text-[12px] font-normal themed-text-muted mt-0.5">in {scene.crumb}</span>}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); onOpenAction(scene.next.id); }}
                    className="flex-shrink-0 text-[13px] themed-text-secondary border themed-border rounded-full px-4 py-1.5 hover:themed-primary hover:themed-primary-border"
                  >
                    Open it →
                  </button>
                </div>
                {scene.factLine && (
                  <p className="text-[11px] themed-text-muted mt-3">{scene.factLine}</p>
                )}
              </div>
            </div>
          )}

          {/* ══ 3 · The ending ══ */}
          {cur === last && (
            <div className="text-center max-w-[520px] mx-auto">
              <p className="text-2xl themed-text-muted mb-4">⌀</p>
              <h1 className="text-3xl themed-text mb-3">That's the rounds.</h1>
              <p className="text-[14px] themed-text-secondary mb-8" style={{ lineHeight: 1.6 }}>
                {leftovers ? 'A few dates are still asking before you go.' : 'Nothing else is waiting.'}
              </p>

              {leftovers && (
                <div className="text-left max-w-[480px] mx-auto mb-8">
                  {rounds.overdue.length > 0 && (
                    <div className="mb-5">
                      <Label>Overdue</Label>
                      {rounds.overdue.map(g => (
                        <button key={g.id} onClick={() => onOpenAction(g.id)} className="w-full text-left flex items-baseline gap-2 py-1 group">
                          <span className="text-[13.5px] leading-snug themed-text group-hover:underline decoration-1 underline-offset-2">{g.text}</span>
                          <span className="text-[9px] font-semibold flex-shrink-0" style={{ color: '#dc2626' }}>due {fmtCivil(g.dueDate, { month: 'short', day: 'numeric' })}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {rounds.missedPlans.length > 0 && (
                    <div className="mb-5">
                      <Label>Slipped plans</Label>
                      {rounds.missedPlans.map(g => (
                        <div key={g.id} className="flex items-baseline gap-2 py-1">
                          <button onClick={() => onOpenAction(g.id)} className="text-left text-[13.5px] leading-snug themed-text opacity-70 hover:opacity-100 hover:underline decoration-1 underline-offset-2">
                            {g.text}
                          </button>
                          <span className="text-[9px] themed-text-muted flex-shrink-0">planned {fmtCivil(g.doDate, { month: 'short', day: 'numeric' })}</span>
                          {updateAction && (
                            <span className="flex items-center gap-1.5 flex-shrink-0 ml-1">
                              <button onClick={() => updateAction(g.id, { doDate: todayStr() })} className="text-[10px] font-medium px-1.5 py-0.5 rounded-full border themed-border themed-text-muted hover:themed-text" title="Replan for today">→ today</button>
                              <button onClick={() => updateAction(g.id, { doDate: null })} className="text-[10px] font-medium px-1.5 py-0.5 rounded-full border themed-border themed-text-muted hover:themed-text" title="Let the plan go — the action stays">release</button>
                            </span>
                          )}
                        </div>
                      ))}
                      <p className="text-[10.5px] themed-text-muted mt-1.5 opacity-70">Never auto-rolled — replan them or let them float free.</p>
                    </div>
                  )}

                  {rounds.dueDeferred.length > 0 && (
                    <div className="mb-2">
                      <button onClick={onOpenDeferred} className="flex items-center gap-2 text-[13px] themed-primary hover:underline">
                        <RotateCcw size={12} />
                        {rounds.dueDeferred.length} deferred action{rounds.dueDeferred.length === 1 ? '' : 's'} came due — review
                      </button>
                    </div>
                  )}
                </div>
              )}

              <button onClick={onClose} className="text-[13px] themed-text border themed-border rounded-full px-6 py-2 hover:themed-primary hover:themed-primary-border">
                Back to your goals →
              </button>
              <button onClick={() => setStep(0)} className="block mx-auto mt-4 text-[12px] themed-text-muted hover:themed-text">
                back to the threshold ↑
              </button>
            </div>
          )}
        </div>
      </div>

      {/* position without numbers — a dot per step */}
      <div className="fixed bottom-8 left-0 right-0 flex justify-center gap-2.5 pointer-events-none">
        {Array.from({ length: last + 1 }).map((_, i) => (
          <button
            key={i}
            onClick={() => setStep(i)}
            className="pointer-events-auto rounded-full"
            style={{
              width: 6, height: 6, padding: 0, border: 'none', cursor: 'pointer',
              background: i === cur ? 'var(--color-text)' : 'var(--color-border)',
              transform: i === cur ? 'scale(1.35)' : 'none',
              transition: 'background 0.25s ease, transform 0.25s ease',
            }}
            title={i === 0 ? 'The threshold' : i === last ? 'The ending' : (rounds.scenes[i - 1] || {}).goal?.name}
          />
        ))}
      </div>
      <p className="fixed bottom-3 left-0 right-0 text-center text-[10px] themed-text-muted opacity-60 pointer-events-none">
        j / k or Enter to walk · R or Esc to close · opens once each morning
      </p>
    </div>
  );
}
